import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { companySettingsApi, companyApi, profileApi, invoiceDesignApi, type CompanyFull, type CompanyMemberItem, type KuStatus, type InvoiceDesignItem } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useCompany } from '../CompanyProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Button, Card, Field, PageHeader, LegalFormSelect, IndustryInput, HelpBadge, euro } from '../ui';
import { useTheme, type ThemeMode } from '../ThemeProvider';
import { ElsterSection } from './ElsterSection';

type Tab = 'profil' | 'firma' | 'team' | 'darstellung' | 'elster';

export default function Settings(): ReactElement {
  const [tab, setTab] = useState<Tab>('profil');
  const { active } = useCompany();
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Einstellungen" subtitle="Profil, Firmendaten, Team und Darstellung verwalten." />
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
        {([['profil', 'Profil'], ['firma', 'Firma'], ['team', 'Team'], ['darstellung', 'Darstellung'], ['elster', 'ELSTER']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${tab === k ? 'border-blue-600 text-blue-700 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>{l}</button>
        ))}
        <Link to="/app/einstellungen/tarif" className="-mb-px border-b-2 border-transparent px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">Tarif & Abo →</Link>
      </div>
      {tab === 'profil' && <ProfilTab />}
      {active && tab === 'firma' && <FirmaTab companyId={active.id} />}
      {active && tab === 'team' && <TeamTab companyId={active.id} />}
      {tab === 'darstellung' && <DarstellungTab />}
      {active && tab === 'elster' && <ElsterSection companyId={active.id} />}
    </div>
  );
}

function DarstellungTab(): ReactElement {
  const { mode, setMode } = useTheme();
  const options: { key: ThemeMode; label: string; hint: string; icon: string }[] = [
    { key: 'light', label: 'Hell', hint: 'Immer helles Design', icon: '☀️' },
    { key: 'dark', label: 'Dunkel', hint: 'Immer dunkles Design', icon: '🌙' },
    { key: 'system', label: 'System', hint: 'Folgt der Geräte-Einstellung', icon: '🖥️' },
  ];
  return (
    <Card className="space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Darstellung</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">Wähle das Erscheinungsbild der App. Gilt nur für den eingeloggten Bereich.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => setMode(o.key)}
            className={`rounded-xl border p-4 text-left transition ${
              mode === o.key
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 dark:bg-blue-950 dark:ring-blue-800'
                : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
            }`}
          >
            <div className="text-2xl">{o.icon}</div>
            <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">{o.label}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{o.hint}</div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function ProfilTab(): ReactElement {
  const { user } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [busy, setBusy] = useState(false);

  async function saveProfile() {
    setBusy(true);
    try { await profileApi.update({ name, email }); toast.success('Profil gespeichert.'); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }
  async function changePw() {
    if (newPw.length < 8) { toast.error('Neues Passwort: mindestens 8 Zeichen.'); return; }
    setBusy(true);
    try { await profileApi.changePassword(curPw, newPw); toast.success('Passwort geändert.'); setCurPw(''); setNewPw(''); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Persönliche Daten</h2>
        <Field label="Name" value={name} onChange={setName} />
        <Field label="E-Mail" type="email" value={email} onChange={setEmail} />
        <div className="flex justify-end"><Button onClick={saveProfile} busy={busy}>Speichern</Button></div>
      </Card>
      <Card className="space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Passwort ändern</h2>
        <Field label="Aktuelles Passwort" type="password" value={curPw} onChange={setCurPw} />
        <Field label="Neues Passwort" type="password" value={newPw} onChange={setNewPw} hint="Mindestens 8 Zeichen." />
        <div className="flex justify-end"><Button onClick={changePw} busy={busy}>Passwort ändern</Button></div>
      </Card>
    </div>
  );
}

function FirmaTab({ companyId }: { companyId: string }): ReactElement {
  const toast = useToast();
  const [c, setC] = useState<CompanyFull | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { companySettingsApi.get(companyId).then(setC).catch((e) => toast.error(errMsg(e))); }, [companyId]);

  function set<K extends keyof CompanyFull>(k: K, v: CompanyFull[K]) { setC((p) => (p ? { ...p, [k]: v } : p)); }

  async function onLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) { toast.error('Logo zu groß (max. 500 KB).'); return; }
    const reader = new FileReader();
    reader.onload = () => set('logoDataUrl', reader.result as string);
    reader.readAsDataURL(file);
  }

  async function save() {
    if (!c) return;
    setBusy(true);
    try {
      await companySettingsApi.update(companyId, {
        name: c.name, legalForm: c.legalForm ?? undefined, taxNumber: c.taxNumber ?? undefined, vatId: c.vatId ?? undefined,
        industry: c.industry ?? undefined,
        kleinunternehmer: c.kleinunternehmer, gewerbesteuer: c.gewerbesteuer ?? false,
        ustvaRhythmus: c.ustvaRhythmus ?? undefined,
        street: c.street ?? undefined, postalCode: c.postalCode ?? undefined,
        city: c.city ?? undefined, email: c.email?.trim() || undefined, phone: c.phone?.trim() || undefined, iban: c.iban ?? undefined,
        bic: c.bic ?? undefined, logoDataUrl: c.logoDataUrl ?? undefined,
      } as Partial<CompanyFull>);
      toast.success('Firmendaten gespeichert.');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  if (!c) return <p className="text-gray-500">Lädt …</p>;

  return (
    <div className="space-y-4">
    <Card className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200">
          {c.logoDataUrl ? <img src={c.logoDataUrl} alt="Logo" className="h-full w-full object-contain" /> : <span className="text-xs text-gray-400">Logo</span>}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" />
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>Logo hochladen</Button>
          {c.logoDataUrl && <button onClick={() => set('logoDataUrl', null)} className="ml-2 text-xs text-red-600 hover:underline">entfernen</button>}
          <p className="mt-1 text-xs text-gray-500">Erscheint auf deinen Rechnungen. Max. 500 KB.</p>
        </div>
      </div>

      <Field label="Firmenname" value={c.name} onChange={(v) => set('name', v)} />
      <div>
        <span className="mb-1.5 block text-sm font-medium text-gray-700">
          Rechtsform
          <HelpBadge text="Die Rechtsform deines Unternehmens (z. B. Einzelunternehmen, GmbH)." />
        </span>
        <LegalFormSelect value={c.legalForm ?? ''} onChange={(v) => set('legalForm', v)} />
      </div>
      <div>
        <span className="mb-1.5 block text-sm font-medium text-gray-700">
          Branche / Tätigkeit
          <HelpBadge text="Deine Branche hilft der KI, Belege korrekt zu kategorisieren – z. B. wird eine Tankquittung im Handwerk als Fahrzeugkosten gebucht, bei Beratern eher als Reisekosten." />
        </span>
        <IndustryInput value={c.industry ?? ''} onChange={(v) => set('industry', v)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700">
            Steuernummer
            <HelpBadge text="Deine Steuernummer vom Finanzamt (z. B. 12/345/67890). Erscheint auf Rechnungen und in Steuermeldungen." />
          </span>
          <input value={c.taxNumber ?? ''} onChange={(e) => set('taxNumber', e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700">
            USt-IdNr.
            <HelpBadge text="Umsatzsteuer-Identifikationsnummer (DE + 9 Ziffern), nötig für EU-Geschäfte. Beim Bundeszentralamt für Steuern beantragbar." />
          </span>
          <input value={c.vatId ?? ''} onChange={(e) => set('vatId', e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
        </label>
      </div>
      <Field label="Straße" value={c.street ?? ''} onChange={(v) => set('street', v)} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="PLZ" value={c.postalCode ?? ''} onChange={(v) => set('postalCode', v)} />
        <Field label="Ort" value={c.city ?? ''} onChange={(v) => set('city', v)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="IBAN" value={c.iban ?? ''} onChange={(v) => set('iban', v)} />
        <Field label="BIC" value={c.bic ?? ''} onChange={(v) => set('bic', v)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="E-Mail" type="email" value={c.email ?? ''} onChange={(v) => set('email', v)} />
        <Field label="Telefon" value={c.phone ?? ''} onChange={(v) => set('phone', v)} />
      </div>
      <p className="-mt-1 text-xs text-gray-500 dark:text-gray-400">
        E-Mail und Telefon werden als Kontakt des Datenlieferanten bei ELSTER-Übermittlungen verwendet.
      </p>
      <label className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <input type="checkbox" checked={c.kleinunternehmer} onChange={(e) => set('kleinunternehmer', e.target.checked)} className="h-5 w-5 rounded border-gray-300 text-blue-600" />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Kleinunternehmer (§19 UStG) – keine USt ausweisen
          <HelpBadge text="Bei Aktivierung weist Saldify auf Rechnungen keine Umsatzsteuer aus (für Kleinunternehmer nach §19 UStG)." />
        </span>
      </label>
      <label className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <input type="checkbox" checked={c.gewerbesteuer ?? false} onChange={(e) => set('gewerbesteuer', e.target.checked)} className="h-5 w-5 rounded border-gray-300 text-blue-600" />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Gewerbesteuerpflichtig
          <HelpBadge text="Gewerbebetriebe (z. B. GmbH, UG, OHG, Handel) zahlen Gewerbesteuer. Freiberufler und Partnerschaftsgesellschaften meist nicht. Steuert die GewSt-Frist im Dashboard." />
        </span>
      </label>
      {!c.kleinunternehmer && (
        <label className="block rounded-xl border border-gray-200 p-3 dark:border-gray-700">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Umsatzsteuer-Voranmeldung (UStVA)
            <HelpBadge text="Wie oft Sie die UStVA abgeben müssen, gibt das Finanzamt vor – abhängig von Ihrer Umsatzsteuer-Zahllast im Vorjahr. Steuert Pflicht und Fristen in der Steuer-Übersicht." />
          </span>
          <select
            value={c.ustvaRhythmus ?? ''}
            onChange={(e) => set('ustvaRhythmus', (e.target.value || null) as 'monthly' | 'quarterly' | 'none' | null)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">Bitte wählen …</option>
            <option value="monthly">Monatlich (Zahllast Vorjahr über 9.000 €)</option>
            <option value="quarterly">Vierteljährlich (Regelfall)</option>
            <option value="none">Jährlich / vom Finanzamt befreit (keine Voranmeldung)</option>
          </select>
        </label>
      )}
      <div className="flex justify-end"><Button onClick={save} busy={busy}>Speichern</Button></div>
    </Card>
    <RechnungsdesignPanel companyId={companyId} current={c.invoiceDesign ?? 'classic'} currentAccent={c.invoiceAccent ?? null} onSaved={() => companySettingsApi.get(companyId).then(setC).catch(() => undefined)} />
    <NummernformatPanel companyId={companyId} invoiceFormat={c.invoiceNumberFormat ?? ''} quoteFormat={c.quoteNumberFormat ?? ''} invoiceStart={c.invoiceNumberStart != null ? String(c.invoiceNumberStart) : ''} quoteStart={c.quoteNumberStart != null ? String(c.quoteNumberStart) : ''} onSaved={() => companySettingsApi.get(companyId).then(setC).catch(() => undefined)} />
    <SkontoPanel companyId={companyId} percent={c.skontoPercent != null ? String(Number(c.skontoPercent)) : ''} days={c.skontoDays != null ? String(c.skontoDays) : ''} onSaved={() => companySettingsApi.get(companyId).then(setC).catch(() => undefined)} />
    <KleinunternehmerJahrPanel companyId={companyId} />
    </div>
  );
}

// Auswahl des Rechnungsdesigns (5 Standard, 5 Premium ab Professional) + Vorschau.
function RechnungsdesignPanel({ companyId, current, currentAccent, onSaved }: { companyId: string; current: string; currentAccent: string | null; onSaved: () => void }): ReactElement {
  const toast = useToast();
  const [designs, setDesigns] = useState<InvoiceDesignItem[]>([]);
  const [sel, setSel] = useState(current);
  const [accent, setAccent] = useState(currentAccent ?? '');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    invoiceDesignApi.list(companyId).then((d) => setDesigns(d.designs)).catch(() => setDesigns([]));
  }, [companyId]);

  async function preview(key: string) {
    try {
      const blob = await invoiceDesignApi.preview(companyId, key, accent || undefined);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    } catch (e) { toast.error(errMsg(e)); }
  }
  async function save() {
    setBusy(true);
    try {
      await companySettingsApi.update(companyId, { invoiceDesign: sel, invoiceAccent: accent || undefined } as Partial<CompanyFull>);
      toast.success('Rechnungsdesign gespeichert.');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <Card className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rechnungsdesign</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">Wähle das Layout deiner Rechnungs-PDFs. Premium-Designs ab Professional.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {designs.map((d) => (
          <button
            key={d.key}
            type="button"
            disabled={d.locked}
            onClick={() => { setSel(d.key); preview(d.key); }}
            className={`relative rounded-xl border p-3 text-left transition ${sel === d.key ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 dark:border-gray-700'} ${d.locked ? 'cursor-not-allowed opacity-50' : 'hover:border-blue-400'}`}
          >
            <span className="block h-2 w-10 rounded-full" style={{ backgroundColor: d.accent }} />
            <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-100">{d.name}</span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400">{d.description}</span>
            {d.tier === 'premium' && (
              <span className="absolute right-2 top-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">{d.locked ? '🔒 Premium' : 'Premium'}</span>
            )}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          Akzentfarbe:
          <input type="color" value={accent || (designs.find((d) => d.key === sel)?.accent ?? '#1d4ed8')} onChange={(e) => setAccent(e.target.value)} className="h-8 w-12 cursor-pointer rounded border border-gray-300" />
        </label>
        <Button variant="secondary" onClick={() => preview(sel)}>Vorschau</Button>
        <Button onClick={save} busy={busy}>Design speichern</Button>
      </div>
      {previewUrl && (
        <iframe title="Design-Vorschau" src={previewUrl} className="h-[480px] w-full rounded-xl border border-gray-200 dark:border-gray-700" />
      )}
    </Card>
  );
}

// Standard-Skonto (Zahlungsbedingung). Wird in neue Rechnungen vorbelegt.
function SkontoPanel({ companyId, percent, days, onSaved }: { companyId: string; percent: string; days: string; onSaved: () => void }): ReactElement {
  const toast = useToast();
  const [p, setP] = useState(percent);
  const [d, setD] = useState(days);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await companySettingsApi.update(companyId, {
        skontoPercent: p.trim() ? Number(p.replace(',', '.')) || 0 : 0,
        skontoDays: d.trim() ? parseInt(d, 10) || 0 : null,
      } as Partial<CompanyFull>);
      toast.success('Skonto gespeichert.');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <Card className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Standard-Skonto</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Wird in neue Rechnungen vorbelegt (je Rechnung änderbar). 0 % = kein Skonto. Verfügbar ab Professional.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Skonto %</span>
          <input value={p} onChange={(e) => setP(e.target.value)} placeholder="z. B. 2"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">innerhalb Tagen</span>
          <input value={d} onChange={(e) => setD(e.target.value)} placeholder="z. B. 14"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
        </label>
      </div>
      <div className="flex justify-end"><Button onClick={save} busy={busy}>Speichern</Button></div>
    </Card>
  );
}

// Frei waehlbares Rechnungs-/Angebotsnummern-Format. Tokens: {YYYY} {YY} {MM} {NNNN}.
function NummernformatPanel({ companyId, invoiceFormat, quoteFormat, invoiceStart, quoteStart, onSaved }: { companyId: string; invoiceFormat: string; quoteFormat: string; invoiceStart: string; quoteStart: string; onSaved: () => void }): ReactElement {
  const toast = useToast();
  const [inv, setInv] = useState(invoiceFormat);
  const [quo, setQuo] = useState(quoteFormat);
  const [invStart, setInvStart] = useState(invoiceStart);
  const [quoStart, setQuoStart] = useState(quoteStart);
  const [busy, setBusy] = useState(false);

  // Spiegelt lib/invoiceNumber.renderNumber: Beispielnummer mit Zaehler + heute.
  function preview(format: string, fallback: string, counter: number): string {
    const tpl = format.trim() || fallback;
    const now = new Date();
    const yyyy = String(now.getFullYear());
    return tpl
      .replace(/\{YYYY\}/g, yyyy)
      .replace(/\{YY\}/g, yyyy.slice(-2))
      .replace(/\{MM\}/g, String(now.getMonth() + 1).padStart(2, '0'))
      .replace(/\{(N+)\}/, (_m, ns: string) => String(counter).padStart(ns.length, '0'));
  }
  function valid(format: string): boolean {
    if (!format.trim()) return true; // leer = Default
    return (format.match(/\{N+\}/g) ?? []).length === 1;
  }
  // Leer = wie bisher automatisch weiterzaehlen. Sonst ganze Zahl >= 1.
  function validStart(v: string): boolean {
    if (!v.trim()) return true;
    return /^\d+$/.test(v.trim()) && parseInt(v.trim(), 10) >= 1;
  }
  function startCounter(v: string): number {
    const n = parseInt(v.trim(), 10);
    return validStart(v) && n >= 1 ? n : 1;
  }

  const presets = ['RE-{NNNN}', 'RG {NNNN}', '{YYYY}-{NNNN}', 'INV-DE-{NNNN}', '{YYYY}{NNNN}'];
  const invOk = valid(inv);
  const quoOk = valid(quo);
  const invStartOk = validStart(invStart);
  const quoStartOk = validStart(quoStart);

  async function save() {
    if (!invOk || !quoOk) { toast.error('Bitte genau einen {NNNN}-Platzhalter verwenden.'); return; }
    if (!invStartOk || !quoStartOk) { toast.error('Die Startnummer muss eine ganze Zahl ab 1 sein.'); return; }
    setBusy(true);
    try {
      await companySettingsApi.update(companyId, {
        invoiceNumberFormat: inv.trim(),
        quoteNumberFormat: quo.trim(),
        invoiceNumberStart: invStart.trim() ? parseInt(invStart.trim(), 10) : null,
        quoteNumberStart: quoStart.trim() ? parseInt(quoStart.trim(), 10) : null,
      } as Partial<CompanyFull>);
      toast.success('Nummernformat gespeichert.');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <Card className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rechnungs- &amp; Angebotsnummern</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Bestimme das Format deiner Nummern. Platzhalter:{' '}
          <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{YYYY}'}</code> Jahr,{' '}
          <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{YY}'}</code> Jahr kurz,{' '}
          <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{MM}'}</code> Monat,{' '}
          <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{NNNN}'}</code> laufende Nummer (Anzahl N = Stellen). Leer = Standard.
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Umstieg aus einem anderen Programm? Trag bei <strong>Startnummer</strong> ein, wo Saldify weiterzählen soll
          (z.&nbsp;B. <strong>26</strong>, wenn deine letzte Rechnung die 0025 war). Leer lassen = wie bisher automatisch.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <button key={p} type="button" onClick={() => setInv(p)}
            className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-700 transition hover:border-blue-400 dark:border-gray-700 dark:text-gray-300">
            {p}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Rechnungsnummer</span>
          <input value={inv} onChange={(e) => setInv(e.target.value)} placeholder="RE-{YYYY}-{NNNN}"
            className={`w-full rounded-xl border px-4 py-2.5 outline-none focus:ring-2 ${invOk ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-200' : 'border-red-400 focus:ring-red-200'}`} />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {invOk ? <>Beispiel: <strong>{preview(inv, 'RE-{YYYY}-{NNNN}', startCounter(invStart))}</strong></> : 'Genau einen {NNNN}-Platzhalter verwenden.'}
          </span>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Startnummer</span>
          <input value={invStart} onChange={(e) => setInvStart(e.target.value)} inputMode="numeric" placeholder="automatisch"
            className={`w-full rounded-xl border px-4 py-2.5 outline-none focus:ring-2 ${invStartOk ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-200' : 'border-red-400 focus:ring-red-200'}`} />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {invStartOk ? 'Leer = automatisch.' : 'Ganze Zahl ab 1.'}
          </span>
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Angebotsnummer</span>
          <input value={quo} onChange={(e) => setQuo(e.target.value)} placeholder="AN-{YYYY}-{NNNN}"
            className={`w-full rounded-xl border px-4 py-2.5 outline-none focus:ring-2 ${quoOk ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-200' : 'border-red-400 focus:ring-red-200'}`} />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {quoOk ? <>Beispiel: <strong>{preview(quo, 'AN-{YYYY}-{NNNN}', startCounter(quoStart))}</strong></> : 'Genau einen {NNNN}-Platzhalter verwenden.'}
          </span>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Startnummer</span>
          <input value={quoStart} onChange={(e) => setQuoStart(e.target.value)} inputMode="numeric" placeholder="automatisch"
            className={`w-full rounded-xl border px-4 py-2.5 outline-none focus:ring-2 ${quoStartOk ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-200' : 'border-red-400 focus:ring-red-200'}`} />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {quoStartOk ? 'Leer = automatisch.' : 'Ganze Zahl ab 1.'}
          </span>
        </label>
      </div>

      <div className="flex justify-end"><Button onClick={save} busy={busy}>Speichern</Button></div>
    </Card>
  );
}

// Kleinunternehmer-Status je Jahr (§19 UStG): Automatik anzeigen + manuell überstimmen.
function KleinunternehmerJahrPanel({ companyId }: { companyId: string }): ReactElement {
  const toast = useToast();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [status, setStatus] = useState<KuStatus | null>(null);
  const [busy, setBusy] = useState(false);

  function load(y: number) {
    companyApi.kuStatus(companyId, y).then(setStatus).catch(() => setStatus(null));
  }
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year, companyId]);

  async function setManual(value: boolean | null) {
    setBusy(true);
    try {
      const s = await companyApi.setKuStatus(companyId, year, value);
      setStatus(s);
      toast.success(value === null ? 'Zurück auf automatische Ermittlung.' : 'Status gespeichert.');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Kleinunternehmer §19 UStG – pro Jahr
            <HelpBadge text="Der Kleinunternehmer-Status hängt vom Jahr ab (Umsatzgrenzen). Wird die Grenze überschritten, bist du im Folgejahr bzw. ab 2025 sofort umsatzsteuerpflichtig. Hier kannst du je Jahr nachsehen und – z. B. bei freiwilliger Regelbesteuerung – manuell festlegen." />
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Maßgeblich für den USt-Ausweis auf Rechnungen dieses Jahres.</p>
        </div>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
      </div>

      {status && (
        <div className="rounded-xl border border-gray-200 p-3 text-sm dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.isKleinunternehmer ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>
              {status.isKleinunternehmer ? 'Kleinunternehmer' : 'Regelbesteuerung'}
            </span>
            <span className="text-xs text-gray-400">{status.source === 'manual' ? 'manuell festgelegt' : 'automatisch ermittelt'}</span>
          </div>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{status.reason}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
            <div>Umsatz Vorjahr: <strong>{euro(status.vorjahrUmsatzCents)}</strong></div>
            <div>Umsatz {status.year}: <strong>{euro(status.laufendUmsatzCents)}</strong></div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" busy={busy} onClick={() => setManual(true)}>Als Kleinunternehmer festlegen</Button>
        <Button variant="secondary" busy={busy} onClick={() => setManual(false)}>Als regelbesteuert festlegen</Button>
        {status?.source === 'manual' && (
          <Button variant="ghost" busy={busy} onClick={() => setManual(null)}>Automatik wiederherstellen</Button>
        )}
      </div>
    </Card>
  );
}

function TeamTab({ companyId }: { companyId: string }): ReactElement {
  const toast = useToast();
  const [members, setMembers] = useState<CompanyMemberItem[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);

  function load() { companySettingsApi.members(companyId).then(setMembers).catch((e) => toast.error(errMsg(e))); }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const roleLabel: Record<string, string> = { owner: 'Inhaber', admin: 'Admin', accountant: 'Steuerberater', member: 'Mitglied' };

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const roleHint: Record<string, string> = {
    member: 'Mitglied: kann Belege und Rechnungen erfassen.',
    admin: 'Admin: voller Zugriff inkl. Einstellungen und Team.',
    accountant: 'Steuerberater: nur Lesezugriff auf die Buchhaltung.',
  };

  async function invite() {
    if (!emailValid) return;
    setBusy(true);
    try { await companySettingsApi.addMember(companyId, email, role); toast.success('Mitglied eingeladen.'); setEmail(''); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between border-b border-gray-50 py-2 last:border-0">
            <div>
              <div className="font-medium text-gray-900">{m.user.name || m.user.email}</div>
              <div className="text-xs text-gray-500">{m.user.email}</div>
            </div>
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">{roleLabel[m.role] ?? m.role}</span>
          </div>
        ))}
      </Card>
      <Card className="space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Mitglied einladen</h2>
        <p className="text-xs text-gray-500">Die Person muss bereits ein Saldify-Konto haben. „Steuerberater" gibt Lesezugriff auf die Buchhaltung.</p>
        <Field label="E-Mail" type="email" inputMode="email" value={email} onChange={setEmail} required placeholder="z. B. kollege@example.com" />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700">Rolle<span className="text-red-500"> *</span></span>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
            <option value="member">Mitglied</option>
            <option value="admin">Admin</option>
            <option value="accountant">Steuerberater</option>
          </select>
          <span className="mt-1 block text-xs text-gray-500">{roleHint[role]}</span>
        </label>
        <div className="flex justify-end"><Button onClick={invite} busy={busy} disabled={!emailValid}>Einladen</Button></div>
      </Card>
    </div>
  );
}
